import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, setDoc } from 'firebase/firestore';
import { safeGetDoc } from '../../utils/firestoreSafeGet';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { ReactComponent as EditIcon } from '../../Icons/edit.svg';
import { auth, db } from '../../firebase/firebaseConfig';
import { USER_PROFILE_QUERY_KEY, selectPrimaryRoleId } from '../../hooks/useUserProfile';
import IconRenderer from '../ui/IconRenderer';
import UserAchievementsList from '../Achievements/UserAchievementsList';
import UserAchievementStatsPanel from '../Achievements/UserAchievementStatsPanel';
import UserProfileMasteredSnippetsList from '../Achievements/UserProfileMasteredSnippetsList';
import {
  COMMUNITY_LIKES_LABEL,
  formatSnippetsMasteredProfileAria,
  SNIPPET_MASTERY_LABEL,
  SNIPPET_MASTERY_TOOLTIP
} from '../../constants/snippetMasteryCopy';
import UserProfileRolesPanel from '../Achievements/UserProfileRolesPanel';
import MasteryBrainIcon from '../Achievements/MasteryBrainIcon';
import { normalizeLanguageKey, fetchTrackMetaBatch, loadInitiatedTrackProgressCards } from '../../services/achievementsService';
import { fetchProfileBootstrap } from '../../services/profileBootstrapCache';
import { loadSnippetsMasteredFromCacheOnly } from '../../services/profileAchievementStatsService';
import './UserProfile.css';
import { useQueryClient } from '@tanstack/react-query';
import { Languages, UserRound, UsersRound } from 'lucide-react';
import { getCommunityLikesReceivedByAuthor } from '../../services/communityCookbookService';
import { formatLanguageDisplay } from '../../utils/roadmapDisplay';

/** IconRenderer `name` for language (matches RoadmapView / deck display). */
function languageIconNameFromKey(labelOrKey) {
  if (!labelOrKey) return 'javascript';
  const k = String(labelOrKey).trim().toLowerCase();
  if (k === 'c++' || k === 'cpp' || k === 'cplusplus') return 'cpp';
  if (k === 'c#' || k === 'csharp') return 'csharp';
  if (k === 'html/css' || k === 'html-css') return 'html';
  if (k === 'shell' || k === 'bash') return 'bash';
  return k.replace(/\s+/g, '');
}

/** Defer non-critical header stats so Skills snippet load gets network priority. */
function scheduleDeferredProfileWork(callback) {
  if (typeof window === 'undefined') {
    callback();
    return () => { };
  }
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(() => callback(), { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 150);
  return () => window.clearTimeout(id);
}

const UserProfile = ({
  userId,
  onStartStudying,
  achievementsPanelRestore,
  onAchievementsPanelRestoreConsumed,
  onOpenCommunity,
}) => {
  const uid = userId || (auth.currentUser ? auth.currentUser.uid : null);
  const canViewOwnAchievements = auth.currentUser?.uid === uid;

  const [userData, setUserData] = useState(null);
  const [userDecks, setUserDecks] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Track if this is the first load
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // XP gain animation state
  const [xpGainAnimations, setXpGainAnimations] = useState([]);

  const queryClient = useQueryClient();

  const handlePrimaryRoleChange = useCallback(
    (roleId) => {
      setUserData((prev) => (prev ? { ...prev, primaryRoleId: roleId } : prev));
      if (uid) {
        queryClient.invalidateQueries([USER_PROFILE_QUERY_KEY, uid]);
      }
    },
    [queryClient, uid]
  );

  // Direct Firestore query for user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!uid) return;
      const userRef = doc(db, 'users', uid);
      try {
        const userSnap = await safeGetDoc(userRef);
        if (userSnap.exists()) {
          setUserData({ ...userSnap.data(), uid });
        } else {
          setUserData(null);
        }
      } catch (e) {
        console.error('UserProfile: fetchUserData failed', e);
        setUserData(null);
      }
    };
    fetchUserData();
  }, [uid]);

  // Function to show XP gain animation
  const showXpGain = (amount, elementId) => {
    const animation = {
      id: Date.now(),
      amount,
      elementId,
      timestamp: Date.now()
    };
    setXpGainAnimations(prev => [...prev, animation]);

    // Remove animation after 2 seconds
    setTimeout(() => {
      setXpGainAnimations(prev => prev.filter(a => a.id !== animation.id));
    }, 2000);
  };

  // Function to get mastery confidence level
  const getMasteryConfidence = (masteryPercentage) => {
    if (masteryPercentage >= 90) return { level: 'Confident', color: '#4ade80', icon: '✅' };
    if (masteryPercentage >= 70) return { level: 'Familiar', color: '#fbbf24', icon: '🟡' };
    if (masteryPercentage >= 50) return { level: 'Learning', color: '#f97316', icon: '🟠' };
    return { level: 'New', color: '#ef4444', icon: '🔴' };
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !userData?.uid || !auth.currentUser) {
      console.error('Missing required data for avatar upload:', {
        file: !!file,
        uid: userData?.uid,
        user: !!auth.currentUser
      });
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size exceeds 5MB limit');
      }

      const storage = getStorage();
      const timestamp = Date.now();
      const fileExtension = (file.name.split('.').pop() || 'png').toLowerCase();
      const fileName = `avatar_${timestamp}.${fileExtension}`;
      const avatarRef = ref(storage, `users/${auth.currentUser.uid}/${fileName}`);

      const imageMimeByExt = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        webp: 'image/webp',
        bmp: 'image/bmp',
        svg: 'image/svg+xml',
        avif: 'image/avif',
        heic: 'image/heic',
        heif: 'image/heif'
      };
      const contentType =
        file.type && file.type.startsWith('image/')
          ? file.type
          : imageMimeByExt[fileExtension] || 'image/png';

      // Upload file with metadata
      const metadata = {
        contentType,
        customMetadata: {
          'originalName': file.name,
          'uploadedBy': auth.currentUser.uid,
          'timestamp': timestamp.toString()
        }
      };

      const uploadResult = await uploadBytes(avatarRef, file, metadata);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      if (!downloadURL) {
        throw new Error('Failed to get download URL');
      }

      // Update Firestore document
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        updatedAt: new Date()
      });

      // Update auth profile
      await updateProfile(auth.currentUser, {
        photoURL: downloadURL
      });

      // Update local state
      setUserData(prev => ({
        ...prev,
        photoURL: downloadURL
      }));
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setUploadError(error.message);

      if (error.code === 'storage/unauthorized') {
        setUploadError('Permission denied. Please try again.');
      } else if (error.code === 'storage/canceled') {
        setUploadError('Upload was cancelled. Please try again.');
      } else if (error.code === 'storage/unknown') {
        setUploadError('An unknown error occurred. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateName = async (trimmedName) => {
    if (!trimmedName || !userData?.uid || !auth.currentUser) {
      // Show error, do not proceed
      console.error('Missing required data for name update:', {
        name: trimmedName,
        uid: userData?.uid,
        user: auth.currentUser?.uid
      });
      alert('Could not update name: user document not found. Please contact support.');
      return;
    }

    try {
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, {
        displayName: trimmedName,
        updatedAt: new Date()
      });

      await updateProfile(auth.currentUser, {
        displayName: trimmedName
      });

      setUserData(prev => ({
        ...prev,
        displayName: trimmedName
      }));
      setIsEditingName(false);

      // Refetch user profile so UI updates with latest Firestore data
      queryClient.invalidateQueries([USER_PROFILE_QUERY_KEY, userData.uid]);
    } catch (error) {
      console.error('Error updating display name:', error);
    }
  };

  const [activeSection, setActiveSection] = useState('achievements');
  /** Normalized language key; empty = all languages. */
  const [achievementsLanguageFilter, setAchievementsLanguageFilter] = useState('');
  /** Achievements tab: tracks list | skills panel | mastered snippets list | roles. */
  const [achievementsPanelView, setAchievementsPanelView] = useState('roles');
  /** Increments when Skills is clicked while Skills is already active — clears track/tag filters on the skills panel. */
  const [skillsTabRefocusNonce, setSkillsTabRefocusNonce] = useState(0);
  /** Languages from the user's cookbook decks only (library), not roadmap catalog or legacy snippets. */
  const [achievementLanguages, setAchievementLanguages] = useState([]);
  const [isLoadingAchievementLanguages, setIsLoadingAchievementLanguages] = useState(false);
  const [showAchievementLanguageDropdown, setShowAchievementLanguageDropdown] = useState(false);
  const achievementLanguageDropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function loadAchievementLanguageOptions() {
      if (!uid) {
        setAchievementLanguages([]);
        return;
      }
      setIsLoadingAchievementLanguages(true);
      try {
        const isOwnProfile = auth.currentUser?.uid === uid;
        const bootstrap = isOwnProfile
          ? await fetchProfileBootstrap(uid)
          : { decksSnap: null, achievementsSnap: null, trackProgressSnap: null };
        const { decksSnap, achievementsSnap, trackProgressSnap } = bootstrap;
        if (cancelled) return;

        const map = new Map();
        const addLang = (raw) => {
          if (!raw || typeof raw !== 'string') return;
          const key = normalizeLanguageKey(raw);
          if (!key || key === 'other') return;
          if (!map.has(key)) map.set(key, formatLanguageDisplay(key));
        };

        if (decksSnap) {
          decksSnap.forEach((docSnap) => {
            const d = docSnap.data();
            const isCookbook = d.type === 'cookbook' || !d.type;
            if (isCookbook) addLang(d.language);
          });
        }

        if (achievementsSnap) {
          achievementsSnap.forEach((docSnap) => {
            const lang = docSnap.data().language;
            if (lang) addLang(lang);
          });
        }

        if (trackProgressSnap && trackProgressSnap.docs.length > 0) {
          const trackIds = trackProgressSnap.docs.map((d) => d.id);
          const metaById = await fetchTrackMetaBatch(trackIds);
          for (const trackId of trackIds) {
            const lang = metaById.get(trackId)?.language;
            if (lang) addLang(lang);
          }
        }

        const list = Array.from(map.entries())
          .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
          .map(([key, label]) => ({ key, label }));
        setAchievementLanguages(list);
      } catch (e) {
        console.error('UserProfile: failed to load achievement language filters', e);
        setAchievementLanguages([]);
      } finally {
        if (!cancelled) setIsLoadingAchievementLanguages(false);
      }
    }
    loadAchievementLanguageOptions();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!achievementsLanguageFilter) return;
    if (isLoadingAchievementLanguages) return;
    const ok = achievementLanguages.some((l) => l.key === achievementsLanguageFilter);
    if (!ok) setAchievementsLanguageFilter('');
  }, [achievementLanguages, achievementsLanguageFilter, isLoadingAchievementLanguages]);

  useEffect(() => {
    if (!achievementsPanelRestore?.token) return;
    const panel = achievementsPanelRestore.achievementsPanelView;
    if (!panel || typeof panel !== 'string') {
      onAchievementsPanelRestoreConsumed?.();
      return;
    }
    setActiveSection('achievements');
    setAchievementsPanelView(panel);
    const restoredLang = achievementsPanelRestore.achievementsLanguageFilter;
    if (typeof restoredLang === 'string') {
      setAchievementsLanguageFilter(restoredLang);
    }
    onAchievementsPanelRestoreConsumed?.();
  }, [
    achievementsPanelRestore?.token,
    achievementsPanelRestore?.achievementsPanelView,
    achievementsPanelRestore?.achievementsLanguageFilter,
    onAchievementsPanelRestoreConsumed
  ]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        achievementLanguageDropdownRef.current &&
        !achievementLanguageDropdownRef.current.contains(e.target)
      ) {
        setShowAchievementLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedAchievementLanguageLabel = useMemo(() => {
    if (!achievementsLanguageFilter) return null;
    return achievementLanguages.find((l) => l.key === achievementsLanguageFilter)?.label ?? null;
  }, [achievementsLanguageFilter, achievementLanguages]);

  const [achievementTrackCards, setAchievementTrackCards] = useState([]);
  const [achievementTracksLoading, setAchievementTracksLoading] = useState(false);
  const [snippetsMasteredCount, setSnippetsMasteredCount] = useState(0);
  const [snippetsMasteredLoading, setSnippetsMasteredLoading] = useState(false);
  const [communityLikesReceived, setCommunityLikesReceived] = useState(0);
  const [communityLikesLoading, setCommunityLikesLoading] = useState(false);

  useEffect(() => {
    if (!uid || !canViewOwnAchievements) {
      return undefined;
    }
    let cancelled = false;
    const cancelSchedule = scheduleDeferredProfileWork(() => {
      if (cancelled) return;
      (async () => {
        setAchievementTracksLoading(true);
        try {
          const { decksSnap, trackProgressSnap } = await fetchProfileBootstrap(uid);
          const list = await loadInitiatedTrackProgressCards(uid, {
            decksSnap,
            trackProgressSnap
          });
          if (!cancelled) setAchievementTrackCards(list);
        } catch (e) {
          console.error('UserProfile: failed to load achievement track cards', e);
          if (!cancelled) setAchievementTrackCards([]);
        } finally {
          if (!cancelled) setAchievementTracksLoading(false);
        }
      })();
    });
    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [uid, canViewOwnAchievements]);

  useEffect(() => {
    if (!uid || !canViewOwnAchievements) {
      return undefined;
    }
    let cancelled = false;
    const cancelSchedule = scheduleDeferredProfileWork(() => {
      if (cancelled) return;
      (async () => {
        setSnippetsMasteredLoading(true);
        try {
          const count = await loadSnippetsMasteredFromCacheOnly(uid, '');
          if (!cancelled) setSnippetsMasteredCount(count ?? 0);
        } catch (e) {
          console.error('UserProfile: failed to load SRS mastered snippet count', e);
          if (!cancelled) setSnippetsMasteredCount(0);
        } finally {
          if (!cancelled) setSnippetsMasteredLoading(false);
        }
      })();
    });
    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [uid, canViewOwnAchievements]);

  useEffect(() => {
    if (!uid || !canViewOwnAchievements) {
      return undefined;
    }
    let cancelled = false;
    const cancelSchedule = scheduleDeferredProfileWork(() => {
      if (cancelled) return;
      (async () => {
        setCommunityLikesLoading(true);
        try {
          const total = await getCommunityLikesReceivedByAuthor(uid);
          if (!cancelled) setCommunityLikesReceived(total);
        } catch (e) {
          console.error('UserProfile: failed to load community likes received', e);
          if (!cancelled) setCommunityLikesReceived(0);
        } finally {
          if (!cancelled) setCommunityLikesLoading(false);
        }
      })();
    });
    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [uid, canViewOwnAchievements]);

  const renderSection = () => {
    switch (activeSection) {
      case 'Teams':
        return (
          <section className="teams-section">
            <div className="section-header">
              {/* <h3>Teams</h3> */}
            </div>
            <div className="placeholder-content">
              <p>Join Teams to collaborate and learn together</p>
              <div className="section-stats">Coming soon</div>
            </div>
          </section>
        );
      case 'achievements':
      default:
        return (
          <section
            className="achievements-section"
            aria-label={
              achievementsPanelView === 'skills'
                ? 'Snippets'
                : achievementsPanelView === 'masteredSnippets'
                  ? 'Mastered snippets'
                  : achievementsPanelView === 'roles'
                    ? 'Roles'
                    : 'Tracks'
            }
            data-language-filter={achievementsLanguageFilter || 'all'}
            data-panel-view={achievementsPanelView}
          >
            {achievementsPanelView === 'skills' ? (
              <UserAchievementStatsPanel
                userId={uid}
                languageFilter={achievementsLanguageFilter}
                skillsTabRefocusNonce={skillsTabRefocusNonce}
                canView={canViewOwnAchievements}
                onStartStudying={onStartStudying}
              />
            ) : achievementsPanelView === 'masteredSnippets' ? (
              <UserProfileMasteredSnippetsList
                userId={uid}
                languageFilter={achievementsLanguageFilter}
                canView={canViewOwnAchievements}
              />
            ) : achievementsPanelView === 'roles' ? (
              <UserProfileRolesPanel
                userId={uid}
                languageFilter={achievementsLanguageFilter}
                canView={canViewOwnAchievements}
                primaryRoleId={selectPrimaryRoleId(userData)}
                onPrimaryRoleChange={handlePrimaryRoleChange}
              />
            ) : (
              <UserAchievementsList
                userId={uid}
                languageFilter={achievementsLanguageFilter}
                canView={canViewOwnAchievements}
                trackCards={achievementTrackCards}
                tracksLoading={achievementTracksLoading}
                externalTracks
              />
            )}
          </section>
        );
    }
  };

  return (
    <div className="user-profile">
      <div className="user-header">
        <div className="user-info">
          <div className="user-profile-user-header__avatar-col">
            <div
              className="profile-avatar"
              onMouseEnter={() => setIsHoveringAvatar(true)}
              onMouseLeave={() => setIsHoveringAvatar(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              {userData?.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt="User avatar"
                  className="avatar-image"
                />
              ) : (
                <div className="profile-icon">
                  {userData?.displayName ? userData.displayName[0].toUpperCase() : 'U'}
                </div>
              )}
              {isHoveringAvatar && (
                <div className="avatar-overlay">
                  <EditIcon />
                  <span>Change</span>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
            {isUploading && (
              <div className="upload-status">
                <div className="upload-spinner"></div>
                <span>Uploading...</span>
              </div>
            )}
            {uploadError && (
              <div className="upload-error">
                {uploadError}
              </div>
            )}
          </div>
          <div className="user-profile-user-header__identity">
            <div className="user-profile-user-header__name-row">
              {isEditingName ? (
                <div className="name-edit-container">
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    className="name-edit-input"
                    placeholder="Enter display name"
                    autoFocus
                  />
                  <div className="name-edit-buttons">
                    <button
                      className="save-button"
                      onClick={() => handleUpdateName(newDisplayName.trim())}
                    >
                      Save
                    </button>
                    <button
                      className="cancel-button"
                      onClick={() => {
                        setIsEditingName(false);
                        setNewDisplayName(userData?.displayName || '');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="name-display">
                  <h2>{userData?.displayName || 'User'}</h2>
                  <button
                    className="edit-name-button"
                    onClick={() => {
                      setNewDisplayName(userData?.displayName || '');
                      setIsEditingName(true);
                    }}
                    title="Edit display name"
                  >
                    <EditIcon />
                  </button>
                </div>
              )}
            </div>
            {canViewOwnAchievements ? (
              <div className="user-profile-user-header__stats-row">
                <div
                  className="user-profile-user-header__achievement-stat-pills"
                  role="group"
                  aria-label="All languages: snippet mastery and community likes"
                >
                  <div
                    className={`user-profile__achievements-stat-pill user-profile__achievements-stat-pill--mastery${!snippetsMasteredLoading && snippetsMasteredCount > 0 ? ' user-profile__achievements-stat-pill--mastery-has' : ''}`}
                    title={SNIPPET_MASTERY_TOOLTIP}
                    aria-label={
                      snippetsMasteredLoading
                        ? 'Snippets mastered across all languages, loading'
                        : formatSnippetsMasteredProfileAria(snippetsMasteredCount)
                    }
                  >
                    <MasteryBrainIcon
                      className="user-profile__achievements-stat-pill__icon user-profile__achievements-stat-pill__icon--brain"
                      aria-hidden
                    />
                    <span className="user-profile-user-header__achievement-stat-pill-copy">
                      <span className="user-profile-user-header__achievement-stat-pill-value">
                        {snippetsMasteredLoading ? '-' : snippetsMasteredCount}
                      </span>
                      <span className="user-profile-user-header__achievement-stat-pill-label">
                        {SNIPPET_MASTERY_LABEL}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`user-profile__achievements-stat-pill user-profile__achievements-stat-pill--community-likes${!communityLikesLoading && communityLikesReceived > 0 ? ' user-profile__achievements-stat-pill--community-likes-has' : ''}`}
                    title="Community likes"
                    aria-label={
                      communityLikesLoading
                        ? 'Community likes on your published cookbooks, loading'
                        : `Community likes on your published cookbooks: ${communityLikesReceived}`
                    }
                  >
                    <i
                      className="ti ti-heart user-profile__achievements-stat-pill__icon user-profile__achievements-stat-pill__icon--heart"
                      aria-hidden="true"
                    />
                    <span className="user-profile-user-header__achievement-stat-pill-copy">
                      <span className="user-profile-user-header__achievement-stat-pill-value">
                        {communityLikesLoading ? '-' : communityLikesReceived}
                      </span>
                      <span className="user-profile-user-header__achievement-stat-pill-label">
                        {COMMUNITY_LIKES_LABEL}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="profile-sections">
        <div className="section-navigation">
          <button
            type="button"
            className={`section-tab user-profile-section-tab ${activeSection === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveSection('achievements')}
          >
            <UserRound className="user-profile-section-tab__icon" aria-hidden strokeWidth={2} />
            <span className="user-profile-section-tab__label">Profile</span>
          </button>
          <button
            type="button"
            className={`section-tab user-profile-section-tab ${activeSection === 'Teams' ? 'active' : ''}`}
            onClick={() => setActiveSection('Teams')}
          >
            <UsersRound className="user-profile-section-tab__icon" aria-hidden strokeWidth={2} />
            <span className="user-profile-section-tab__label">Teams</span>
          </button>
        </div>
        {activeSection === 'achievements' && (
          <div
            className="user-profile__achievements-panel"
            role="region"
            aria-label="Profile language filter and sub-views"
          >
            <div className="user-profile__achievements-panel__row">
              <div
                className="user-profile__achievements-filter-group"
                role="toolbar"
                aria-label="Language, roles, snippets, and tracks"
              >
                <div className="user-profile__achievements-language-wrap" ref={achievementLanguageDropdownRef}>
                  <button
                    type="button"
                    className={`user-profile__achievements-language-trigger ${showAchievementLanguageDropdown ? 'user-profile__achievements-language-trigger--open' : ''}`}
                    onClick={() => {
                      if (isLoadingAchievementLanguages || achievementLanguages.length === 0) return;
                      setShowAchievementLanguageDropdown((o) => !o);
                    }}
                    disabled={isLoadingAchievementLanguages || achievementLanguages.length === 0}
                    aria-label="Select language for profile views"
                    aria-expanded={showAchievementLanguageDropdown}
                    aria-haspopup="listbox"
                  >
                    {isLoadingAchievementLanguages ? (
                      <>
                        <Languages className="user-profile__achievements-language-trigger-pill-icon" aria-hidden />
                        <span className="user-profile__achievements-language-trigger-value">Loading…</span>
                      </>
                    ) : achievementLanguages.length === 0 ? (
                      <>
                        <Languages className="user-profile__achievements-language-trigger-pill-icon" aria-hidden />
                        <span className="user-profile__achievements-language-trigger-value">No languages</span>
                      </>
                    ) : (
                      <>
                        {!achievementsLanguageFilter ? (
                          <Languages className="user-profile__achievements-language-trigger-pill-icon" aria-hidden />
                        ) : (
                          <IconRenderer
                            name={languageIconNameFromKey(selectedAchievementLanguageLabel || achievementsLanguageFilter)}
                            type="language"
                            size="small"
                            className="user-profile__achievements-language-trigger-pill-icon user-profile__achievements-language-trigger-icon"
                          />
                        )}
                        <span className="user-profile__achievements-language-trigger-value">
                          {!achievementsLanguageFilter
                            ? 'All languages'
                            : (selectedAchievementLanguageLabel || achievementsLanguageFilter).toLowerCase()}
                        </span>
                      </>
                    )}
                    <span className="user-profile__achievements-language-trigger-arrow" aria-hidden>
                      ▼
                    </span>
                  </button>
                  {showAchievementLanguageDropdown && achievementLanguages.length > 0 && (
                    <div className="user-profile__achievements-language-dropdown" role="listbox">
                      <button
                        type="button"
                        role="option"
                        aria-selected={!achievementsLanguageFilter}
                        className={`user-profile__achievements-language-option ${!achievementsLanguageFilter ? 'user-profile__achievements-language-option--selected' : ''}`}
                        onClick={() => {
                          setAchievementsLanguageFilter('');
                          setShowAchievementLanguageDropdown(false);
                        }}
                      >
                        <span>All languages</span>
                      </button>
                      {achievementLanguages.map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          role="option"
                          aria-selected={achievementsLanguageFilter === key}
                          className={`user-profile__achievements-language-option ${achievementsLanguageFilter === key ? 'user-profile__achievements-language-option--selected' : ''}`}
                          onClick={() => {
                            setAchievementsLanguageFilter(key);
                            setShowAchievementLanguageDropdown(false);
                          }}
                        >
                          <IconRenderer
                            name={languageIconNameFromKey(label)}
                            type="language"
                            size="small"
                            className="user-profile__achievements-language-option-icon"
                          />
                          <span>{label.toLowerCase()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={`user-profile__achievements-subnav-btn user-profile__achievements-roles-toggle-btn ${activeSection === 'achievements' && achievementsPanelView === 'roles' ? 'user-profile__achievements-roles-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setActiveSection('achievements');
                    setAchievementsPanelView('roles');
                  }}
                  aria-pressed={activeSection === 'achievements' && achievementsPanelView === 'roles'}
                  aria-label="Show roles: role-based learning paths"
                >
                  Roles
                </button>
                <button
                  type="button"
                  className={`user-profile__achievements-subnav-btn user-profile__achievements-stats-toggle-btn ${activeSection === 'achievements' && achievementsPanelView === 'skills' ? 'user-profile__achievements-stats-toggle-btn--active' : ''}`}
                  onClick={() => {
                    const alreadyOnSnippets =
                      activeSection === 'achievements' && achievementsPanelView === 'skills';
                    setActiveSection('achievements');
                    setAchievementsPanelView('skills');
                    if (alreadyOnSnippets) {
                      setSkillsTabRefocusNonce((n) => n + 1);
                    }
                  }}
                  aria-pressed={activeSection === 'achievements' && achievementsPanelView === 'skills'}
                  aria-label="Show snippets: mastery and levels. Press again while on Snippets to clear track and tag filters."
                >
                  Snippets
                </button>
                {canViewOwnAchievements ? (
                  <button
                    type="button"
                    className={`user-profile__achievements-subnav-btn user-profile__achievements-tracks-toggle-btn${achievementsPanelView === 'tracks' ? ' user-profile__achievements-tracks-toggle-btn--active' : ''}`}
                    title={
                      achievementsLanguageFilter
                        ? 'Initiated tracks in this language'
                        : 'All initiated tracks'
                    }
                    aria-label="Show all initiated tracks in progress"
                    aria-pressed={achievementsPanelView === 'tracks'}
                    onClick={() => {
                      setActiveSection('achievements');
                      setAchievementsPanelView('tracks');
                    }}
                  >
                    Tracks
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {renderSection()}

      {/* XP Gain Animations */}
      {xpGainAnimations.map(animation => (
        <div
          key={animation.id}
          className="xp-gain-animation"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          +{animation.amount} XP
        </div>
      ))}
    </div>
  );
};

export default UserProfile;
